import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import CreateCategoryModal from './CreateCategoryModal';
import CreateProjectActivityModal from './CreateProjectActivityModal';
import EditCategoryModal from './EditCategoryModal';
import EditProjectActivityModal from './EditProjectActivityModal';
import CriticalPathPill from './gantt_chart/CriticalPathPill';
import GanttChart from './gantt_chart/GanttChart';
import useGanttLinks from './gantt_chart/useGanttLinks';
import PeriodSelector from './PeriodSelector';
import ProjectActivityActionsModal from './ProjectActivityActionsModal';
import ProjectOwnerControl from './ProjectOwnerControl';
import ProjectPlanDeleteAllModal from './ProjectPlanDeleteAllModal';
import useProjectPlanColumns from './useProjectPlanColumns';
import useProjectPlanScheduleView from './useProjectPlanScheduleView';
import useProjectPlanView, { VIEW_HIERARCHY, VIEW_SOURCE } from './useProjectPlanView';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import SegmentedControl from 'CommonComponents/SegmentedControl';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import ToggleInput from 'CommonComponents/ToggleInput';

import ProjectPeriodContext from 'Context/project_plan/ProjectPeriodContext';
import ProjectPlanContext, { FILTER_MAP_KEYS, selectedFilterMaps } from 'Context/project_plan/ProjectPlanContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useDropdown from 'Hooks/useDropdown';
import useModal from 'Hooks/useModal';
import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

import ChartTableIcon from 'Assets/icons/chart-table.svg';
import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import CircleCancelIcon from 'Assets/icons/circle-xmark.svg';
import CloudArrowUpIcon from 'Assets/icons/cloud-arrow-up.svg';
import DotsIcon from 'Assets/icons/dots-horizontal.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import MinusCircleIcon from 'Assets/icons/minus-circle.svg';
import PlusIcon from 'Assets/icons/plus.svg';
import TrashIcon from 'Assets/icons/trash.svg';

// The first thing a keyboard user should land on inside a panel: a menu item, or the leading
// checkbox of the Columns group. Ordered by document position, not by selector, so whichever
// comes first in the panel wins.
const PANEL_FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

// Stable ids so each trigger can point `aria-controls` at its own panel, and so the Add menu can
// name its hint as its accessible description.
const ADD_MENU_ID = 'project-plan-add-menu';
const ADD_MENU_HINT_ID = 'project-plan-add-menu-hint';
const COLUMNS_PANEL_ID = 'project-plan-columns-panel';
const MORE_MENU_ID = 'project-plan-more-menu';

/**
 * A toolbar button that opens a panel beneath itself. `useDropdown` supplies the positioning,
 * viewport clamping and click-outside handling; the panel is mounted only while open, so the
 * page doesn't carry hidden panels around in `<body>`.
 *
 * `children` may be a render function, which receives `close` so an item can dismiss the panel
 * before it acts.
 */
const ToolbarMenu = ({ id, label, icon, variant = 'outline', caret = true, panelRole = 'menu', panelLabel, panelDescriptionId, children }) => {
    const { triggerRef, dropdownRef, isOpen, close, toggle } = useDropdown();

    // The panel is portalled to the end of <body>, so without this a keyboard user who opened it
    // would find its contents at the very end of the document tab order — one Tab would take them
    // back into the toolbar with the panel still hanging open. Focus therefore moves into the
    // panel on open and returns to the trigger on close.
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const panel = dropdownRef.current;
        const trigger = triggerRef.current;

        panel?.querySelector(PANEL_FOCUSABLE)?.focus();

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);

            // This cleanup runs after the commit that removed the panel, so if focus had been
            // inside it the browser has already reset it to <body>. That is the signal used to
            // tell "the panel closed from under the focused element" (Escape, or an item that
            // acted) from "the user moved to another control", where focus already belongs
            // somewhere else and yanking it back would be wrong. A modal opened by an item still
            // wins, because it claims focus when it mounts, after this runs.
            if (!document.activeElement || document.activeElement === document.body) {
                trigger?.querySelector('button')?.focus();
            }
        };
    }, [isOpen, close, dropdownRef, triggerRef]);

    return (
        <div ref={triggerRef} className="tw-inline-flex">
            <Button
                variant={variant}
                // `menu` only where the panel really is a menu of commands. The Columns panel is a
                // `group` of checkboxes, and announcing "has dialog popup" there would promise a
                // dialog that never arrives.
                aria-haspopup={panelRole === 'menu' ? 'menu' : true}
                aria-expanded={isOpen}
                // Only while open: the panel doesn't exist when closed, so an IDREF to it would
                // dangle.
                aria-controls={isOpen ? id : undefined}
                onClick={toggle}
            >
                {icon}
                {label}
                {caret && <ChevronDownIcon className="tw-w-2 tw-h-2" aria-hidden="true" />}
            </Button>
            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        id={id}
                        role={panelRole}
                        aria-label={panelLabel}
                        aria-describedby={panelDescriptionId}
                        className="tw-min-w-[13rem] tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-1.5 tw-text-start tw-shadow-primary"
                    >
                        {typeof children === 'function' ? children(close) : children}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

const ToolbarMenuItem = ({ label, icon, disabled, destructive, onClick }) => (
    <button
        type="button"
        role="menuitem"
        disabled={disabled}
        onClick={onClick}
        className={classNames(
            'tw-flex tw-w-full tw-items-center tw-gap-x-2 tw-rounded tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium hover:tw-bg-background disabled:tw-cursor-not-allowed disabled:tw-text-charcoal-50',
            destructive ? 'tw-text-red' : 'tw-text-charcoal',
        )}
    >
        {icon}
        <span>{label}</span>
    </button>
);

const ProjectPlanPage = ({
    exportProjectPlanUrl,
    importProjectPlanUrl,
    storeProjectCategoryUrl,
    updateProjectCategoryUrl,
    fetchProjectActivityUrl,
    storeProjectActivityUrl,
    updateProjectActivityUrl,
    fetchProjectActivityActionsUrl,
    fetchDepartmentsOptionsUrl,
    fetchVenuesOptionsUrl,
    updateProjectOwnerUrl,
    projectOwner = null,
    canEditProjectOwner = false,
    deleteAllPlans = [],
}) => {
    const [currentActivity, setCurrentActivity] = useState();
    const [currentCategory, setCurrentCategory] = useState();
    const [currentCategoryId, setCurrentCategoryId] = useState();
    const [filters, setFilters] = useState({});
    const [filterSearch, setFilterSearch] = useState('');

    const { users } = useUsers();

    // Column visibility lives here rather than in the chart: the Columns panel that drives it is
    // part of this toolbar, and the chart derives its entire column layout from the result.
    const { columns, toggle: toggleColumn, definitions: columnDefinitions } = useProjectPlanColumns();

    const {
        toggleAllRows,
        ganttChart,
        statuses,
        sourceCategories,
        filters: _filters,
        changeFilters,
        deletable,
        toggleDeletable,
        removeProjectPlanItems,
        deletableRows,
        isMaster,
        schedule,
        scheduleFailed,
        hasKeyMilestones,
        changeTarget,
    } = useContext(ProjectPlanContext);
    const { period, changePeriod } = useContext(ProjectPeriodContext);

    // Hierarchy vs By source. Offered on the central plan only — see the hook.
    const { view, setView, bySource, canToggle: canToggleView } = useProjectPlanView(isMaster);

    const [scheduleView, setScheduleViewKey] = useProjectPlanScheduleView();
    const ganttLinks = useGanttLinks(scheduleView.links);

    // Known only once the schedule has loaded; until then the switch stays usable.
    const noKeyMilestones = !!schedule && !hasKeyMilestones;
    const criticalPathOn = scheduleView.critical_path && !noKeyMilestones;
    // Highlighting needs the floats, so it waits for the schedule and stays off if it failed.
    const highlightCriticalPath = criticalPathOn && !!schedule && !scheduleFailed;

    // The float-0 activities of the plan on screen, or null while the switch is off.
    const criticalActivityIds = useMemo(() => {
        if (!highlightCriticalPath) {
            return null;
        }

        return new Set(
            Object.entries(schedule.floats || {})
                .filter(([, float]) => float === 0)
                .map(([id]) => Number(id)),
        );
    }, [highlightCriticalPath, schedule]);

    // The switch shows the Float column whatever the Columns panel says.
    const ganttColumns = useMemo(() => ({ ...columns, float: columns.float || criticalPathOn }), [columns, criticalPathOn]);

    const createCategoryModal = useModal();
    const editCategoryModal = useModal();
    const activityActionsModal = useModal();
    const createActivityModal = useModal();
    const editActivityModal = useModal();
    const deleteAllModal = useModal();

    const toast = useToast();

    const confirm = useConfirmationModal();

    const activityRequest = useQuery({
        enabled: false,
        queryFn: () => axios.get(fetchProjectActivityUrl.replace('ACTIVITY_ID', currentActivity.id)),
    });

    const activityActionsRequest = useQuery({
        enabled: false,
        queryFn: () => axios.get(fetchProjectActivityActionsUrl.replace('ACTIVITY_ID', currentActivity.id)),
    });

    const departmentsOptionsRequest = useQuery({
        enabled: isMaster,
        queryFn: () => axios.get(fetchDepartmentsOptionsUrl),
    });

    const venuesOptionsRequest = useQuery({
        enabled: isMaster,
        queryFn: () => axios.get(fetchVenuesOptionsUrl),
    });

    const sortedDepartmentsOptions = useMemo(() => {
        if (!departmentsOptionsRequest.data) return departmentsOptionsRequest.data;
        const TYPE_ORDER = ['App\\GeneralRevdepartment', 'App\\Revdepartment', 'App\\Department'];
        const byLabel = (a, b) => a.label.localeCompare(b.label);
        return departmentsOptionsRequest.data.map((group) => {
            const roots = group.options.filter((o) => o.level === 0);
            const childrenByParent = group.options
                .filter((o) => o.level > 0)
                .reduce((acc, child) => {
                    (acc[child.parentValue] ||= []).push(child);
                    return acc;
                }, {});
            const rootsSorted = [...roots].sort((a, b) => {
                const ai = TYPE_ORDER.indexOf(a.type);
                const bi = TYPE_ORDER.indexOf(b.type);
                if (ai !== bi) return ai - bi;
                return byLabel(a, b);
            });
            const ordered = [];
            rootsSorted.forEach((root) => {
                ordered.push(root);
                (childrenByParent[root.value] || []).sort(byLabel).forEach((k) => ordered.push(k));
            });
            return { ...group, options: ordered };
        });
    }, [departmentsOptionsRequest.data]);

    useEffect(() => {
        if (currentActivity) {
            activityRequest.refetch();
            activityActionsRequest.refetch();
        }
    }, [currentActivity]);

    useEffect(() => setFilters(_filters), [_filters]);

    const showCreateCategoryModal = (categoryId = null) => {
        setCurrentCategoryId(categoryId);
        createCategoryModal.show();
    };

    const showEditCategoryModal = (data) => {
        setCurrentCategory(data);
        setCurrentCategoryId(data.id);
        editCategoryModal.show();
    };

    const showActivityActionsModal = (activity) => {
        setCurrentActivity(activity);
        activityActionsModal.show();
    };

    const showEditActivityModal = (activity) => {
        setCurrentActivity(activity);
        editActivityModal.show();
    };

    const showCreateActivityModal = (categoryId) => {
        setCurrentCategoryId(categoryId);
        setCurrentActivity(null);
        createActivityModal.show();
    };

    const updateFilters = (newFilters) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
    };

    const onDeleteClick = () => {
        if (!deletable && deleteAllPlans.length > 1) {
            deleteAllModal.show();

            return;
        }

        confirm({
            title: $t('Deleting Items'),
            message: deletable ? $t('Are you sure you want to delete these items?') : $t('Are you sure you want to delete all items?'),
            onConfirm: () => {
                removeProjectPlanItems();
            },
        });
    };

    const hasRows = !!ganttChart.data?.rows?.length && !ganttChart.filteredEmpty;

    /**
     * The filter's contents as data, because the wireframe's redesign — a search box, groups with
     * their own "select group", a global select/clear and a running count — needs to walk the
     * options generically. Several groups share one filter map: each department grouping writes
     * into `source_departments`, so the group's `key` is the map it belongs to, not its identity.
     */
    const filterGroups = useMemo(() => {
        const groups = [
            {
                id: 'owners',
                key: 'owners',
                label: $t('Owner'),
                options: users.map((user) => ({ value: String(user.id), label: `${user.first_name} ${user.last_name}` })),
            },
            { id: 'statuses', key: 'statuses', label: $t('Status'), options: statuses.map((status, index) => ({ value: String(index), label: status })) },
            {
                id: 'schedule',
                key: 'schedule',
                label: $t('Schedule'),
                options: [
                    { value: 'critical', label: $t('Critical only') },
                    { value: 'near_critical', label: $t('Near-critical') },
                    { value: 'date_conflicts', label: $t('Date conflicts') },
                    { value: 'no_links', label: $t('Unlinked') },
                    { value: 'not_linked_to_target', label: $t('Not linked to target') },
                ],
            },
        ];

        if (isMaster) {
            groups.push({
                id: 'sources',
                key: 'source_categories',
                label: $t('Source'),
                options: sourceCategories.map((source) => ({ value: String(source.value), label: source.label })),
            });

            (sortedDepartmentsOptions || []).forEach((group) => {
                groups.push({
                    id: `departments-${group.label}`,
                    key: 'source_departments',
                    label: group.label,
                    // Cost centres sit under their department, as they did before.
                    options: group.options.map((option) => ({
                        value: String(option.value),
                        label: option.label,
                        indent: ['App\\Costcenter', 'App\\Revcostcenter'].includes(option.type),
                    })),
                });
            });

            if (venuesOptionsRequest.data?.length) {
                groups.push({
                    id: 'venues',
                    key: 'source_venues',
                    label: $t('Venues'),
                    options: venuesOptionsRequest.data.map((venue) => ({ value: String(venue.id), label: venue.label })),
                });
            }
        }

        return groups.filter((group) => group.options.length);
    }, [users, statuses, isMaster, sourceCategories, sortedDepartmentsOptions, venuesOptionsRequest.data]);

    // The search narrows the options themselves, and a group with nothing left drops out — so a
    // long department list can be got at without scrolling past everything else.
    const visibleFilterGroups = useMemo(() => {
        const term = filterSearch.trim().toLowerCase();

        if (!term) {
            return filterGroups;
        }

        return filterGroups
            .map((group) => ({ ...group, options: group.options.filter((option) => option.label.toLowerCase().includes(term)) }))
            .filter((group) => group.options.length);
    }, [filterGroups, filterSearch]);

    /**
     * The panel's draft, shown in its footer. Counted against the options the panel offers, so
     * unticked entries and keys the panel cannot show are left out, and against `filterGroups`
     * rather than `visibleFilterGroups` so it does not tick down as the search box narrows the list.
     */
    const selectedFilterCount = useMemo(
        () => filterGroups.reduce((total, group) => total + group.options.filter((option) => filters[group.key]?.[option.value]).length, 0),
        [filterGroups, filters],
    );

    // What the plan is actually filtered by, shown on the trigger. Counted from the applied maps
    // themselves, so it matches the request even when an option list has not loaded or is denied.
    const appliedFilterCount = useMemo(
        () => Object.values(selectedFilterMaps(_filters)).reduce((total, map) => total + Object.keys(map).length, 0),
        [_filters],
    );

    /**
     * Reduce each map to the selections that are still both offered and ticked, so the dead keys
     * stop being sent with every request and are dropped from the saved filters on the next save.
     *
     * Unticked entries go too. The gantt request is a GET and the controller only honours `'true'`,
     * so a `false` is already inert — but a composite department key encodes to ~55 bytes, and
     * "Clear all" writes one per option. On an event with a few hundred cost centres that is enough
     * query string to hit the 414 that broke the roadmap pills in IB-781.
     *
     * A map is only pruned when at least one group currently drives it. With no such group the
     * options have simply not arrived yet — the departments/venues requests are still in flight, or
     * this is not the master plan and never offers them — and wiping a legitimate saved selection
     * because Apply was pressed early would be far worse than carrying a stale key.
     */
    const pruneFilters = () => {
        const allowedValues = {};

        filterGroups.forEach((group) => {
            const values = (allowedValues[group.key] ||= new Set());
            group.options.forEach((option) => values.add(option.value));
        });

        const pruned = { ...filters };

        FILTER_MAP_KEYS.forEach((key) => {
            if (!allowedValues[key]) {
                return;
            }

            pruned[key] = Object.fromEntries(Object.entries(filters[key] || {}).filter(([value, checked]) => checked && allowedValues[key].has(value)));
        });

        return pruned;
    };

    const setGroupSelection = (group, checked) =>
        updateFilters({
            [group.key]: { ...(filters[group.key] || {}), ...Object.fromEntries(group.options.map((option) => [option.value, checked])) },
        });

    // Applies to what is currently listed, so "Select all" under a search means "all of these",
    // not "everything including the rows you have filtered out of sight".
    const setAllSelection = (checked) => {
        const next = {};

        // Clearing with no panel search resets every map, including ones no visible group drives
        // (an Owner list the viewer cannot load), so a saved selection the panel cannot show is
        // still removable.
        if (!checked && !filterSearch.trim()) {
            FILTER_MAP_KEYS.forEach((key) => {
                next[key] = {};
            });
        }

        visibleFilterGroups.forEach((group) => {
            next[group.key] = {
                ...(filters[group.key] || {}),
                ...next[group.key],
                ...Object.fromEntries(group.options.map((option) => [option.value, checked])),
            };
        });

        updateFilters(next);
    };

    // Filter closes the toolbar row, drawn as an outline button rather than the round icon this
    // table used before so it reads as a peer of Columns and More rather than as table chrome.
    const filtersModal = (
        <TableFilterModal
            id="project-plan-table_filter-modal"
            onApply={() => changeFilters(pruneFilters())}
            triggerLabel={
                <>
                    {$t('Filter')}
                    {appliedFilterCount > 0 && (
                        <span className="tw-ms-1.5 tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-full tw-bg-blue tw-text-2xs tw-font-semibold tw-text-white">
                            {appliedFilterCount}
                        </span>
                    )}
                </>
            }
            triggerClassName="button outline"
            isActive={appliedFilterCount > 0}
            sectioned
            // 330px is the wireframe's card. Below it a long grouping name — "Revenue
            // departments" — collides with its own "select group" link.
            modalBodyClassNames="tw-w-[330px]"
            // No Cancel: the wireframe's footer is the count and Apply alone, and "Clear all"
            // in the header now does what Cancel did.
            showCancel={false}
            headerActions={
                <span className="tw-whitespace-nowrap tw-text-xs tw-text-charcoal-50">
                    <button type="button" className="tw-text-blue" onClick={() => setAllSelection(true)}>
                        {$t('Select all')}
                    </button>
                    <span className="tw-mx-1">·</span>
                    <button type="button" className="tw-text-blue" onClick={() => setAllSelection(false)}>
                        {$t('Clear all')}
                    </button>
                </span>
            }
            footerStatus={<span className="tw-text-xs tw-text-charcoal-50">{$t('{{count}} selected', { count: selectedFilterCount })}</span>}
        >
            <div className="tw-max-h-96 tw-overflow-y-auto">
                <input
                    type="text"
                    value={filterSearch}
                    onChange={(event) => setFilterSearch(event.target.value)}
                    placeholder={$t('Search filters')}
                    className="tw-mb-2.5 tw-w-full tw-rounded-lg tw-border tw-border-[#e3e6ea] tw-px-2.5 tw-py-[7px] tw-text-[13px]"
                />
                {visibleFilterGroups.map((group) => (
                    <div key={group.id}>
                        <div className="tw-mb-1 tw-mt-2 tw-flex tw-items-baseline tw-justify-between tw-gap-x-3">
                            {/* The name yields, the link does not: a long grouping name truncates
                                rather than pushing "select group" out of the card. */}
                            <span className="tw-truncate tw-text-[11px] tw-uppercase tw-tracking-[0.04em] tw-text-charcoal-50" title={group.label}>
                                {group.label}
                            </span>
                            <button type="button" className="tw-shrink-0 tw-text-xs tw-text-blue" onClick={() => setGroupSelection(group, true)}>
                                {$t('select group')}
                            </button>
                        </div>
                        {group.options.map((option) => (
                            <div key={`${group.id}-${option.value}`} className={classNames('tw-py-1', { 'tw-ps-4': option.indent })}>
                                <CheckboxInput
                                    id={`${group.id}-${option.value}`}
                                    checked={filters[group.key]?.[option.value] || false}
                                    value={option.value}
                                    label={option.label}
                                    onChange={(event) =>
                                        updateFilters({
                                            [group.key]: { ...(filters[group.key] || {}), [event.target.value]: event.target.checked },
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                ))}
                {!visibleFilterGroups.length && <div className="tw-py-3 tw-text-xs tw-text-charcoal-50">{$t('No matching filters')}</div>}
            </div>
        </TableFilterModal>
    );

    return (
        // `inbud-wireframe-face` gives every .button inside the wireframe's soft-rectangle
        // geometry instead of the app's pill, without restyling buttons elsewhere.
        <div className="table__card inbud-wireframe-face tw-flex tw-flex-col tw-h-full">
            <div className="tw-flex tw-flex-col tw-min-h-0 tw-h-full tw-space-y-4">
                {/* Primary toolbar: adding and the two view switches on the left, everything else
                    grouped on the right. One row, not a row plus a config panel above it — every
                    surface reaches this page through a tab already labelled "Project Plan", so the
                    panel's heading only repeated it and, with the heading gone, there was nothing
                    left inside the tile to collapse. Nothing destructive sits out here: Delete Many
                    and Delete All live under "More". */}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <ToolbarMenu
                        id={ADD_MENU_ID}
                        variant="primary"
                        label={$t('Add')}
                        icon={<PlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />}
                        panelLabel={$t('Add')}
                        panelDescriptionId={ADD_MENU_HINT_ID}
                    >
                        {(close) => (
                            // No wrapping element: `role="menu"` only admits menu items, groups,
                            // separators and presentational nodes, so a plain <div> here would
                            // leave assistive tech reporting a menu with no items in it.
                            <>
                                <ToolbarMenuItem
                                    label={$t('Category')}
                                    onClick={() => {
                                        close();
                                        showCreateCategoryModal();
                                    }}
                                />
                                {/* Sub-categories and activities have no parent picker of their own — both
                                    modals are handed the category they belong to — so they stay where that
                                    parent is unambiguous: the row itself. */}
                                <div role="separator" className="tw-border-t tw-border-grey tw-mx-1 tw-my-1.5" />
                                {/* Named as the panel's accessible description, because with the old icon
                                    legend gone this sentence is the only place that instruction appears. */}
                                <p role="presentation" id={ADD_MENU_HINT_ID} className="tw-px-3 tw-pb-1 tw-mb-0 tw-text-2xs tw-text-charcoal-50">
                                    {$t('Sub-categories and activities are added from a category row.')}
                                </p>
                            </>
                        )}
                    </ToolbarMenu>
                    <span className="tw-text-2xs tw-text-charcoal-50">{$t('category · sub-category · activity')}</span>

                    {/* The two view switches, grouped so they wrap together and keep a wider gap from
                        the Add hint than from each other. Both read their labels inline rather than
                        stacked: a caption above one control in a row of buttons would lift the pair
                        off the baseline the rest of the row sits on. */}
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-ms-2">
                        {canToggleView && (
                            <div className="tw-flex tw-items-center tw-gap-x-2">
                                <span className="inbud-control-label !tw-mb-0">{$t('View')}</span>
                                <SegmentedControl
                                    variant="track"
                                    options={[
                                        { value: VIEW_HIERARCHY, label: $t('Hierarchy') },
                                        { value: VIEW_SOURCE, label: $t('By source') },
                                    ]}
                                    value={view}
                                    onChange={setView}
                                />
                            </div>
                        )}
                        <PeriodSelector inline period={period} onChange={(selectedPeriod) => changePeriod(selectedPeriod)} />
                        <ToggleInput
                            id="project-plan-links"
                            label={$t('Links')}
                            checked={scheduleView.links}
                            onChange={(e) => setScheduleViewKey('links', e.target.checked)}
                            noBottomSpacing
                        />
                        <ToggleInput
                            id="project-plan-critical-path"
                            label={$t('Critical path')}
                            checked={scheduleView.critical_path}
                            disabled={noKeyMilestones}
                            tooltip={noKeyMilestones ? $t('No key milestones') : undefined}
                            noBottomSpacing
                            checkedClassName="peer-checked:tw-bg-red"
                            onChange={(event) => setScheduleViewKey('critical_path', event.target.checked)}
                        />
                    </div>

                    <div className="tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        {updateProjectOwnerUrl && (
                            <div className="tw-me-2">
                                <ProjectOwnerControl updateUrl={updateProjectOwnerUrl} initialOwner={projectOwner} canEdit={!!canEditProjectOwner} />
                            </div>
                        )}
                        {importProjectPlanUrl && (
                            <Button as="a" href={importProjectPlanUrl} variant="outline">
                                <CloudArrowUpIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />
                                {$t('Upload')}
                            </Button>
                        )}
                        <Button as="a" href={exportProjectPlanUrl} variant="outline">
                            <DownloadIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />
                            {$t('Download')}
                        </Button>
                        <ToolbarMenu
                            id={COLUMNS_PANEL_ID}
                            label={$t('Columns')}
                            icon={<ChartTableIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />}
                            caret={false}
                            panelRole="group"
                            panelLabel={$t('Show columns')}
                        >
                            <div className="tw-px-2 tw-py-1 tw-space-y-2">
                                <div className="tw-text-xs tw-font-semibold tw-text-blue">{$t('Show columns')}</div>
                                {columnDefinitions
                                    // Source is absent from the by-source view — the band carries it
                                    // — so offering a switch that would do nothing is worse than
                                    // dropping the row for as long as that view is on.
                                    .filter((definition) => (!definition.masterOnly || isMaster) && !(definition.key === 'source' && bySource))
                                    .map((definition) => (
                                        <CheckboxInput
                                            key={definition.key}
                                            id={`project-plan-column-${definition.key}`}
                                            // The Critical path switch holds Float open while it is on.
                                            checked={ganttColumns[definition.key]}
                                            disabled={definition.key === 'float' && criticalPathOn}
                                            onChange={() => toggleColumn(definition.key)}
                                            label={
                                                <span className="tw-text-xs tw-text-charcoal">
                                                    {definition.label}
                                                    {definition.hint && <span className="tw-ms-1 tw-text-2xs tw-text-charcoal-50">({definition.hint})</span>}
                                                </span>
                                            }
                                        />
                                    ))}
                            </div>
                        </ToolbarMenu>
                        <ToolbarMenu
                            id={MORE_MENU_ID}
                            variant="outline-danger"
                            label={$t('More')}
                            icon={<DotsIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />}
                            panelLabel={$t('More')}
                        >
                            {(close) => (
                                // Unwrapped, for the same `role="menu"` reason as the Add menu.
                                <>
                                    <ToolbarMenuItem
                                        destructive={!deletable}
                                        icon={
                                            deletable ? (
                                                <CircleCancelIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />
                                            ) : (
                                                <MinusCircleIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />
                                            )
                                        }
                                        label={deletable ? $t('Cancel Delete') : $t('Delete Many')}
                                        disabled={!hasRows}
                                        onClick={() => {
                                            close();
                                            toggleDeletable();
                                        }}
                                    />
                                    <ToolbarMenuItem
                                        destructive
                                        icon={<TrashIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />}
                                        label={deletable ? $t('Delete Selected') : $t('Delete All')}
                                        disabled={(deletable && !deletableRows.size) || !hasRows}
                                        onClick={() => {
                                            close();
                                            onDeleteClick();
                                        }}
                                    />
                                </>
                            )}
                        </ToolbarMenu>
                        {filtersModal}
                    </div>
                </div>

                {/* Secondary row: expand/collapse and search. Neither is in the wireframe, but both
                    work today and neither belongs in the primary action row. */}
                <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2 tw-justify-between tw-items-end">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2">
                        <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                            <div className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Row view')}</div>
                            <div className="tw-flex tw-gap-x-1.5">
                                <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllRows()}>
                                    {$t('Expand all')}
                                </button>
                                <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllRows(false)}>
                                    {$t('Collapse all')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-gap-x-3.5 tw-gap-y-2">
                        {scheduleView.critical_path && <CriticalPathPill schedule={schedule} failed={scheduleFailed} onChangeTarget={changeTarget} />}
                        <TableSearchInput
                            searchValue={filters.search || ''}
                            handleChange={(e) => updateFilters({ search: e.target.value })}
                            handleKeyPress={(e) => e.key === 'Enter' && changeFilters({ search: filters.search })}
                        />
                    </div>
                </div>

                <GanttChart
                    columns={ganttColumns}
                    bySource={bySource}
                    criticalPath={highlightCriticalPath}
                    criticalActivityIds={criticalActivityIds}
                    onSubcategoryCreate={showCreateCategoryModal}
                    onCategoryEdit={showEditCategoryModal}
                    onActivityActionsShow={showActivityActionsModal}
                    onActivityEdit={showEditActivityModal}
                    onActivityCreate={showCreateActivityModal}
                    // For the empty state's two ways to start a plan. Wrapped rather than passed
                    // directly so the modal opens with no parent category, which is what a
                    // top-level category needs.
                    onCategoryCreate={() => showCreateCategoryModal()}
                    importProjectPlanUrl={importProjectPlanUrl}
                    links={ganttLinks}
                />
            </div>

            <CreateCategoryModal
                id="CREATE_CATEGORY_MODAL"
                modal={createCategoryModal}
                categoryId={currentCategoryId}
                storeUrl={storeProjectCategoryUrl}
                onCreated={() => {
                    ganttChart.refetch();
                }}
            />

            <EditCategoryModal
                id="EDIT_CATEGORY_MODAL"
                modal={editCategoryModal}
                category={currentCategory}
                updateUrl={updateProjectCategoryUrl}
                onUpdated={() => {
                    ganttChart.refetch();
                }}
            />

            <ProjectActivityActionsModal
                id="PROJECT_ACTIVITY_ACTIONS_MODAL"
                modal={activityActionsModal}
                activity={currentActivity}
                isLoading={activityActionsRequest.loading}
                actions={activityActionsRequest.data || []}
            />

            <CreateProjectActivityModal
                id="CREATE_PROJECT_ACTIVITY_MODAL"
                modal={createActivityModal}
                categoryId={currentCategoryId}
                isLoading={activityActionsRequest.loading || activityRequest.loading}
                storeUrl={storeProjectActivityUrl}
                updateUrl={updateProjectActivityUrl}
                onCreated={() => {
                    toast.success($t('Activity saved successfully'));
                    ganttChart.refetchAfterEdit();
                }}
            />

            <EditProjectActivityModal
                id="EDIT_PROJECT_ACTIVITY_MODAL"
                modal={editActivityModal}
                activityId={currentActivity?.id}
                categoryId={currentCategoryId}
                activity={activityRequest.data || {}}
                actions={activityActionsRequest.data || []}
                isLoading={activityActionsRequest.loading || activityRequest.loading}
                storeUrl={storeProjectActivityUrl}
                updateUrl={updateProjectActivityUrl}
                onCreated={() => {
                    toast.success($t('Activity saved successfully'));
                    ganttChart.refetchAfterEdit();
                }}
            />

            <ProjectPlanDeleteAllModal
                id="PROJECT_PLAN_DELETE_ALL_MODAL"
                modal={deleteAllModal}
                plans={deleteAllPlans}
                onConfirm={(projectIds) => removeProjectPlanItems(projectIds)}
            />
        </div>
    );
};

export default ProjectPlanPage;
